/**
 * 由 CoCo 源代码计划解包器解包
 *
 * 模块 ID：540
 */

import classNames from "classnames"
import { memo, useRef, useState, useCallback, useEffect, CSSProperties, ReactNode } from "react"
import React from "react"
import ReactDom from "react-dom"
import "./styles.css"
import { IconFont } from "../Iconfont"
import { useInnerWidth } from "../../hooks/use-inner-width"
import { useInnerHeight } from "../../hooks/use-inner-height"
import { useOffsetWidth } from "../../hooks/use-offset-width"
import { useOffsetHeight } from "../../hooks/use-offset-height"

export interface IDialogProps {
  className?: classNames.Argument
  style?: CSSProperties
  visible: boolean
  maskClosable?: boolean
  mask?: boolean
  onClose?(): void
  children?: ReactNode
  showCloseButton?: boolean
  withPortal?: boolean
  title?: string
  footer?: ReactNode
}

export const Dialog = memo(({
  className,
  style,
  visible,
  maskClosable = false,
  mask = true,
  onClose,
  children,
  showCloseButton = true,
  withPortal = false,
  title,
  footer
}: IDialogProps) => {

  const elementRef = useRef<HTMLDivElement>(null)
  const wrapperElementRef = useRef<HTMLDivElement>(null)
  const contentElementRef = useRef<HTMLDivElement>(null)
  const [isPlayingAnimation, setIsPlayingAnimation] = useState(false)

  let containerElement = document.getElementById("_cocoDialogContainer")
  if (!containerElement) {
    containerElement = document.createElement("div")
    containerElement.id = "_cocoDialogContainer"
    document.body.appendChild(containerElement)
  }

  const onCloseCallback = useCallback(() => {
    if (onClose) {
      onClose()
    }
  }, [onClose])

  const close = useCallback(() => {
    if (maskClosable) {
      onCloseCallback()
    }
  }, [onCloseCallback, maskClosable])

  // [CoCo Next] 缩小显示过大的对话框
  const innerWidth = useInnerWidth()
  const innerHeight = useInnerHeight()
  const wrapperElementWidth = useOffsetWidth(wrapperElementRef)
  const wrapperElementHeight = useOffsetHeight(wrapperElementRef)
  useEffect(() => {
    const wrapperElement = wrapperElementRef.current
    if (wrapperElement === null) {
      return
    }
    const zoomX = innerWidth / wrapperElementWidth
    const zoomY = innerHeight / wrapperElementHeight
    const zoom = Math.min(zoomX, zoomY, 1)
    if (visible && isPlayingAnimation) {
      wrapperElement.style.transform = `translate(-50%, -50%) scale(${zoom})`
    } else {
      wrapperElement.style.transform = ""
      if (visible) {
        setTimeout(() => { setIsPlayingAnimation(true) }, 10)
      }
    }
  }, [wrapperElementRef, visible, isPlayingAnimation, innerWidth, innerHeight, wrapperElementWidth, wrapperElementHeight])

  useEffect(() => {
    const element = elementRef.current
    function listener(event: KeyboardEvent) {
      if (!(contentElementRef.current?.contains(event.target as HTMLElement))) {
        event.stopPropagation()
      }
    }
    if (element) { element.addEventListener("keydown", listener) }
    return () => { element?.removeEventListener("keydown", listener) }
  }, [])

  // [CoCo Next] 优化动画
  useEffect(() => {
    if (!visible && isPlayingAnimation) {
      const handle = setTimeout(() => { setIsPlayingAnimation(false) }, 300)
      return () => { clearTimeout(handle) }
    }
    return undefined
  }, [visible, wrapperElementWidth, isPlayingAnimation])

  // [CoCo Next] 优化
  const node = (visible || isPlayingAnimation) && <div
    data-html2canvas-ignore="true"
    ref={elementRef}
    tabIndex={-1}
    className={classNames("coco-dialog", className)}
    style={{
      // [CoCo Next] 优化动画
      display: (visible || isPlayingAnimation) ? "block" : "none",
      ...style
    }}
  >
    {mask && <div
      className={visible && isPlayingAnimation ? "coco-dialog-mask" : classNames("coco-dialog-mask", "hidden")}
      onClick={close}
    />}
    <div className="coco-dialog-scroll">
      <div
        className={visible && isPlayingAnimation ? classNames("coco-dialog-wrapper", "show") : classNames("coco-dialog-wrapper", "hidden")}
        // [CoCo Next] 优化动画
        // onAnimationEnd={() => {
        //   if (!visible) {
        //     setIsPlayingAnimation(false)
        //   }
        // }}
        ref={wrapperElementRef}
      >
        <div className={title && showCloseButton ? "coco-dialog-title" : ""}>
          {title && <div>{title}</div>}
          {showCloseButton && <span
            className="coco-dialog-close"
            onClick={onCloseCallback}
          >
            <IconFont type="icon-close" />
          </span>}
        </div>
        <div className="coco-dialog-content" ref={contentElementRef}>{children}</div>
        {footer && <div className="coco-dialog-footer">{footer}</div>}
      </div>
    </div>
  </div>
  return withPortal ? ReactDom.createPortal(node, containerElement) : node
})
export { Dialog as a }
